import { Link } from "react-router-dom";
import { useEffect } from "react";
import { motion } from "framer-motion";
import Hero from "../components/Hero";
import Section from "../components/Section";
import StatCard from "../components/StatCard";
import ServiceCard from "../components/ServiceCard";
import AccreditationGrid from "../components/AccreditationGrid";
import CTA from "../components/CTA";
import { ScrollReveal } from "../components/animations/ScrollReveal";
import { stats } from "../data/stats";
import { serviceCategories, serviceBenefits } from "../data/services";
import { certifications } from "../data/certifications";
import { updatePageMeta, updateStructuredData } from "../lib/seo";
import { getOrganizationSchema } from "../lib/schema";
import img1 from '../assets/home/img1.avif';
import img2 from '../assets/home/img2.avif';
import img3 from '../assets/home/img3.avif';
import img7 from '../assets/home/about.jpg';

function Home() {
  const defaultImages = [
    img1,
    img2,
    img3
  ];
  useEffect(() => {
    updatePageMeta({
      title:
        "Terra Environ Labs - Environmental Testing Laboratory",
      description:
        "Leading environmental engineering and testing laboratory offering comprehensive solutions in air quality, water testing, waste management, and environmental compliance consultancy.",
    });
    updateStructuredData(getOrganizationSchema());
  }, []);

  return (
    <>
      <Hero
       
      />

      {/* Stats Section */}
      {/* <Section background="white">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {stats.map((stat) => (
            <StatCard key={stat.id} {...stat} />
          ))}
        </div>
      </Section> */}

      {/* Services Section */}
      <Section
        id="services"
        title="Our Services"
        subtitle="End-to-end environmental solutions tailored to your industry and regulatory requirements"
        background="gray"
      >


        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 mb-12">
          {serviceCategories.map((category, index) => (
            <div key={category.id} className="flex">
              <ServiceCard
                title={category.title}
                description={category.description}
                // icon={category.icon}
                href={category.href}
                features={category.services.slice(0, 4).map((s) => s.name)}
                image={defaultImages[index % defaultImages.length]} 
              />
            </div>
          ))}
        </div>

        {/* Service Benefits */}
        <div className="bg-white border border-accent-200 rounded-lg p-6 md:p-8">
          <div className="text-center mb-8 md:mb-10">
            <h3 className="text-xl md:text-2xl font-semibold text-accent-900 mb-2">
              Why Choose Our Services
            </h3>
            <div className="w-12 h-0.5 bg-primary-600 mx-auto"></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {serviceBenefits.map((benefit, index) => (
              <motion.div 
                key={index} 
                className="p-4 md:p-5 border border-accent-200 rounded-lg bg-white hover:border-primary-300 hover:bg-accent-50/50 transition-all duration-200"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-50px' }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ y: -4, borderColor: 'rgb(59, 130, 246)' }}
              >
                <div className="flex items-start">
                  <div className="flex-shrink-0 w-10 h-10 rounded-md bg-primary-50 flex items-center justify-center text-lg mr-3 border border-primary-100">
                    {benefit.icon}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-semibold text-base text-accent-900 mb-1.5 leading-tight">
                      {benefit.title}
                    </h4>
                    <p className="text-xs md:text-sm text-accent-600 leading-relaxed">
                      {benefit.description}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </Section>

      {/* Certifications Section */}
      <Section
        id="certifications"
        title="Accreditations & Certifications"
        subtitle="Recognized by national and international bodies for quality and technical competence"
        background="white"
      >
        <div>
          <AccreditationGrid certifications={certifications.slice(0, 6)} />
        </div>
        <ScrollReveal delay={0.2} className="text-center mt-10">
          <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
            <Link to="/certifications" className="btn-outline inline-flex items-center">
              View All Certifications
              <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </Link>
          </motion.div>
        </ScrollReveal>
      </Section>

      {/* About Preview */}
      <Section background="pattern">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <ScrollReveal delay={0.1}>
            <h2 className="text-3xl md:text-4xl font-bold text-accent-900 mb-4">
              Multidisciplinary Expertise in Environmental Science
            </h2>
            <p className="text-lg text-accent-600 mb-6 leading-relaxed">
              With over 15 years of experience, we bring together environmental
              engineers, chemists, microbiologists, and consultants to deliver
              reliable, accurate, and actionable insights for your environmental
              challenges.
            </p>
            <ul className="space-y-3 mb-8">
              {[
                "State-of-the-art laboratory facilities",
                "NABL-accredited testing procedures",
                "Experienced technical team",
                "Rapid turnaround times",
                "Comprehensive reporting",
                "Onsite training and support",
              ].map((item, index) => (
                <motion.li 
                  key={index} 
                  className="flex items-center text-accent-700"
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: '-50px' }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                >
                  <svg
                    className="w-5 h-5 text-primary-600 mr-3 flex-shrink-0"
                    fill="none"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path d="M5 13l4 4L19 7" />
                  </svg>
                  {item}
                </motion.li>
              ))}
            </ul>
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Link to="/about" className="btn-primary inline-flex items-center">
                Learn More About Us
                <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </Link>
            </motion.div>
          </ScrollReveal>
          <ScrollReveal delay={0.2} direction="right">
            <motion.div 
              className="relative"
              whileHover={{ scale: 1.02 }}
              transition={{ duration: 0.3 }}
            >
              <div className="aspect-square bg-gradient-to-br from-primary-100 to-secondary-100 rounded-2xl flex items-center justify-center overflow-hidden shadow-xl">
                <img src={img7} alt="ISO 17025" className="w-full h-full object-cover"/>
              </div>
            </motion.div>
          </ScrollReveal>
        </div>
      </Section>

      {/* CTA Section */}
      <CTA
        title="Ready to Get Started?"
        description="Contact us today for a consultation or request a quote for your environmental testing and consultancy needs."
        primaryText="Request Quote"
        primaryLink="/enquiry"
        secondaryText="Contact Us"
        secondaryLink="/contact"
      />
    </>
  );
}

export default Home;
